import {
  createContext,
  Fragment,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import { Provider } from 'react-aria-components'
import {
  LazyMotion,
  LayoutGroup,
  m,
  useTransform,
  useMotionValueEvent,
  useReducedMotion,
  useSpring,
  AnimatePresence,
} from 'motion/react'
import { shuffleTransition } from '@/consts/springs'
import { useElementSize, useScrollOffsets } from '@/hooks/element-hooks'
import {
  animationFrameScheduler,
  BehaviorSubject,
  filter,
  fromEvent,
  merge,
  NEVER,
  type Observable,
  of,
  pairwise,
  switchMap,
  take,
} from 'rxjs'
import {
  delay,
  distinctUntilChanged,
  map,
  observeOn,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { ConversationMessageComposerContext } from '@/features/messages/components/ConversationMessageComposer'
import {
  useObservable,
  useObservableValue,
  useSubject,
} from '@/hooks/rx-hooks.ts'
import { shareLatest, whileActive } from '@/utils/rx-util'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import styles from './ConversationLayout.module.css'
import { mergeProps } from 'react-aria'

const loadMotionFeatures = () =>
  import('@/infra/motion-features').then((r) => r.default)

export interface ConversationLayoutProps {
  /**
   * Component to display at the very top of the conversation
   */
  header?: ReactNode

  /**
   * Leading content before the messages.
   */
  children?: ReactNode

  /**
   * The composer view.
   */
  composer?: ReactNode

  /**
   * Whether to use a composer mask. Can be turned off if undesired.
   *
   * @default 'visible'
   */
  composerMask?: 'visible' | 'none'

  /**
   * The message list.
   */
  messageList?: ReactNode

  /**
   * By default, a button to scroll to the bottom will appear once scrolled up.
   * This can be disabled by passing in `true`.
   */
  disallowScrollingToBottom?: boolean

  /**
   * If specified, the scrolling element will be scrolled to the bottom
   * when the observable emits.
   * The emitted values are {@link ScrollToBottomOptions}.
   */
  scrollToBottom$?: Observable<ScrollToBottomOptions | undefined>

  /**
   * If specified, will wait with performing scroll requests until this emits
   * `true`.
   */
  isReadyToScroll$?: Observable<boolean>

  /**
   * Represents the element that serves as the scrollable container for the
   * conversation list.
   *
   * To use the overall page as the scroll container, omit or pass in `null`.
   */
  scrollingElement?: Element | null

  /**
   * Label for the "scroll to bottom" button
   */
  scrollButtonLabel?: string

  /**
   * Which positioning scheme to use.
   *
   * 1. `position: sticky` - this is the safest in terms of positioning,
   *     and should be used when a `scrollingElement` is specified, since that
   *     indicates that a custom scroll container is being used.
   *
   * 2. `position: fixed` + a sentinel element for reading the `width` and
   *     occupying the composer's actual height to make it appear as if it's
   *     part of the flow.
   *     This should be used when the composer is always going to be positioned
   *     at the bottom of the viewport.
   *
   * @default 'fixed'
   */
  composerPositioning?: 'fixed' | 'sticky'

  /**
   * Whether to hide the composer visually. It'll still occupy however much
   * space it did before.
   */
  isComposerHidden?: boolean

  /**
   * Spacing to apply on the sides.
   */
  spacing?: 'default' | 'compact'

  /**
   * List of people who are currently typing.
   */
  typing?: string[]

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Options for scrolling to the bottom.
 */
export interface ScrollToBottomOptions {
  /**
   * Scroll behavior.
   */
  behavior?: 'smooth' | 'instant'
  /**
   * If `true`, the scroll will only be performed while anchored.
   */
  onlyWhenAnchored?: boolean
}

/**
 * Default stream for `isReadyToScroll$`.
 */
const defaultIsReadyToScroll$ = of(true)

/**
 * Context for passing props to a {@link ConversationLayout}.
 */
export const ConversationLayoutContext = createContext<
  Partial<ConversationLayoutProps>
>({})

/**
 * A layout for a conversation, including the message list and a composer.
 */
export function ConversationLayout(props: ConversationLayoutProps) {
  const contextProps = use(ConversationLayoutContext)
  const {
    composer,
    messageList,
    scrollingElement,
    children,
    disallowScrollingToBottom,
    scrollToBottom$,
    isReadyToScroll$ = defaultIsReadyToScroll$,
    typing,
    scrollButtonLabel = 'View latest messages',
    composerPositioning = 'fixed',
    spacing = 'default',
    isComposerHidden = false,
    composerMask = 'visible',
    testId,
    header,
  } = mergeProps(contextProps, props)

  // Tracks whether the user has scrolled to the bottom of the list,
  // which will trigger sticky scrolling.
  const isAnchoredRef = useRef(true)

  // Whether the scroll button is inert (won't respond to presses or focus).
  const [scrollButtonInert, setScrollButtonInert] = useState(false)

  // We'll show the scroll-to-bottom button once scrolled a certain amount
  const scrollToBottomPossibleAt = 1000

  // Subscribe to scroll offsets, specifically for the `y`.
  // This lets us know whether we're "anchored" to the bottom, so we can
  // keep scrolling to the bottom when requested to do so.
  const { connectRef: connectScrollRef, scrollYEnd } = useScrollOffsets()
  useMotionValueEvent(scrollYEnd, 'change', (value) => {
    // Allow a small buffer for stickiness. When we've scrolled more than
    // this, we won't auto-scroll to the bottom.
    isAnchoredRef.current = value < 200
    setScrollButtonInert(value < scrollToBottomPossibleAt)
  })

  const scrollButtonContainerY = useTransform(
    scrollYEnd,
    [scrollToBottomPossibleAt, scrollToBottomPossibleAt + 50],
    [-20, 0],
  )
  const scrollButtonContainerOpacity = useTransform(
    scrollYEnd,
    [scrollToBottomPossibleAt, scrollToBottomPossibleAt + 20],
    [0, 1],
  )

  // When using `fixed` composer positioning, we'll render a sentinel element
  // which will be used to know how wide the fixed container needs to be, as
  // well as occupy a composers-worth of height.
  const composerSentinelRef = useRef<HTMLDivElement>(null)
  const {
    connectRef: connectSentinelRefToElementSize,
    size: { width: sentinelWidth },
    size$: sentinelSize$,
  } = useElementSize()
  const connectSentinelRef = useComposedRefs(
    composerSentinelRef,
    connectSentinelRefToElementSize,
  )

  // Get the actual composer's height; the sentinel will be used to
  // make it appear as if the fixed container is sticky and takes up space
  // when scrolled to the bottom.
  const composerRef = useRef<HTMLDivElement>(null)
  const {
    connectRef: connectComposerRefToElementSize,
    size$: composerSize$,
    sizeMotionValues: composerSizeMotionValues,
  } = useElementSize()
  const connectComposerRef = useComposedRefs(
    composerRef,
    connectComposerRefToElementSize,
  )

  // We need to break it up into these 3 due to some retarded behavior with
  // Motion where it would be animating towards the wrong value after
  // the initial render. It would only be correct once a change in the
  // input height triggered a recalculation. But breaking it out like this
  // seems to work.
  //
  // The key is to have the spring be on the numerical value rather than the
  // string-interpolated value.
  const scrollToBottomButtonOffsetValue = useTransform(() => {
    const height = composerSizeMotionValues.height.get()
    return Math.max(24, Math.round(isComposerHidden ? 0 : height - 24))
  })
  const scrollToBottomButtonOffsetSpring = useSpring(
    scrollToBottomButtonOffsetValue,
    shuffleTransition,
  )
  const scrollButtonContainerYWithOffset = useTransform(
    () => scrollButtonContainerY.get() - scrollToBottomButtonOffsetSpring.get(),
  )

  // We need to track when the composer is focused, so we can blur it when
  // scrolling via drag on mobile.
  const isComposerFocused$ = useSubject(() => new BehaviorSubject(false))
  const isComposerFocused = useObservableValue(isComposerFocused$)
  const blur$ = useSubject()
  const composerFocusChange = useCallback(
    (isFocused: boolean) => isComposerFocused$.next(isFocused),
    [isComposerFocused$],
  )

  const shouldReduceMotion = useReducedMotion()

  // Scrolls to the bottom.
  const scrollToBottom = useCallback(
    (behavior: ScrollToBottomOptions['behavior'] = 'smooth') => {
      const effectiveBehavior = shouldReduceMotion ? 'instant' : behavior

      // Prefer scrolling the target into view on mobile when the composer
      // is focused (soft keyboard likely to be open). This avoids the
      // Mobile Safari overscroll issue when triggering a scroll-to-bottom
      // while the keyboard is open. The heuristic for detecting this is
      // when the visual viewport is smaller than
      // the window, and the composer is focused.
      const target = composerSentinelRef.current ?? composerRef.current
      if (
        target &&
        isComposerFocused$.value &&
        visualViewport &&
        visualViewport.height < window.innerHeight
      ) {
        target.scrollIntoView({
          behavior: effectiveBehavior,
          block: 'end',
          inline: 'nearest',
        })
        return
      }

      if (!scrollingElement) {
        return
      }

      scrollingElement.scrollTo({
        top: scrollingElement.scrollHeight,
        behavior: effectiveBehavior,
      })
    },
    [scrollingElement, isComposerFocused$, shouldReduceMotion],
  )

  // Handler for the scroll-to-bottom button
  const scrollToBottomPressHandler = useCallback(
    () => scrollToBottom('smooth'),
    [scrollToBottom],
  )

  // Wire up the scrolling element to the scroll tracker
  useEffect(() => {
    if (!scrollingElement) {
      return
    }

    return connectScrollRef(scrollingElement)
  }, [scrollingElement, connectScrollRef])

  // Set up scroll-to-bottom requests.
  // If we're rendering a composer, then we'll have to wait for
  // the composer's size to be reported before we trigger the sync.
  // To do that, we'll create an observable that emits a value indicating
  // whether we're ready to accept scroll requests.
  // The composer size to look at depends on whether we're using the sentinel
  // or not. This is because when `fixed`, the sentinel's height gets updated
  // only _after_ the fixed composer's height has updated. To make sure
  // the browser takes the composer's new height into account when scrolling,
  // we have to wait for the sentinel's height to update first.
  // On the other hand, if we're not using the sentinel, we simply need to look
  // at the actual composer's height.
  const effectiveComposerHeight$ = useMemo(
    () =>
      (composerPositioning === 'fixed' ? sentinelSize$ : composerSize$).pipe(
        map((size) => size.height),
        distinctUntilChanged(),
      ),
    [composerSize$, sentinelSize$, composerPositioning],
  )

  // Next, we'll make an observable that emits a `boolean` indicating whether
  // we're ready to accept scroll requests based on whether the composer's
  // height has been reported, as well as an optional consumer-supplied
  // observable.
  const isReadyToAcceptScrollRequests$ = useMemo(
    () =>
      effectiveComposerHeight$
        .pipe(
          // We know we're rendering a composer, so it's
          // bound to eventually have a size. We'll wait
          // with emitting until it does.
          map((height) => height > 0),
        )
        .pipe(distinctUntilChanged(), shareLatest()),
    [effectiveComposerHeight$],
  )

  // Make sure we have at least once subscriber,
  // that way it'll emit immediately when subscribed to in the
  // "scroll to bottom when ready" pipeline due to the `shareLatest`.
  // Additionally, we'll render the UI with opacity 0 until we're ready
  // to avoid a flash jump when scrolling.
  const isReadyToAcceptScrollRequests = useObservableValue(
    isReadyToAcceptScrollRequests$,
    { initial: false },
  )

  // Defers emitting scroll requests until we're ready to accept them.
  const scrollToBottomWhenReady$ = useMemo(
    () =>
      (scrollToBottom$ ?? NEVER).pipe(
        switchMap((options) =>
          isReadyToAcceptScrollRequests$.pipe(
            filter(Boolean),
            switchMap((isReadyCore) =>
              !isReadyCore ? of(false) : isReadyToScroll$,
            ),
            take(1),
            map(() => options ?? {}),
          ),
        ),
        // Accessing the ref here is perfectly fine.
        // eslint-disable-next-line react-compiler/react-compiler
        switchMap((options) => {
          // If we only want to scroll when anchored, and we aren't
          // currently anchored, then skip this one.
          if (options.onlyWhenAnchored && !isAnchoredRef.current) {
            return NEVER
          }

          // Otherwise, continue.
          return options.onlyWhenAnchored
            ? // If only when anchored, then it's very likely that this request
              // to scroll is in response to a new message having been added
              // to the list. It's possible that by the time we trigger the
              // scroll, it hasn't been fully rendered yet, so scrolling
              // wouldn't scroll all the way.
              // To mitigate this, we'll defer the scroll by 2 frames to be safe.
              of(options.behavior).pipe(
                // Delay by 2 frames, this ensures the new message (if any)
                // has had time to be rendered.
                observeOn(animationFrameScheduler),
                observeOn(animationFrameScheduler),
              )
            : of(options.behavior)
        }),
      ),
    [scrollToBottom$, isReadyToAcceptScrollRequests$, isReadyToScroll$],
  )

  // Wire up the requests pipeline to trigger scrolling.
  useObservable(scrollToBottomWhenReady$, scrollToBottom)

  // Sets up hacks for mobile.
  useEffect(() => {
    // On mobile, scrolling while the keyboard is open
    // results in the composer not being fixed to the bottom anymore.
    // Since it's impossible to actually make that work, we simply blur
    // the composer when we detect touch-based scrolling.
    const touchStart$ = fromEvent(scrollingElement ?? window, 'touchstart')
    const touchEnd$ = fromEvent(scrollingElement ?? window, 'touchend')
    const scroll$ = fromEvent(scrollingElement ?? window, 'scroll')
    const blurOnTouchScroll$ = touchStart$.pipe(
      switchMap(() =>
        // When we get a scroll event
        scroll$.pipe(
          take(1),
          // Stop listening for scroll events on `touchend`
          takeUntil(touchEnd$.pipe(delay(200))),
        ),
      ),
      // Only while the composer is focused.
      whileActive(isComposerFocused$),
      // Signals to the composer that we want to blur the textarea.
      tap(() => blur$.next(undefined)),
    )

    // On iOS Safari, when the page is scrolled to all the way to the bottom,
    // and the height of the composer decreases due to the input shrinking
    // (e.g., the user deletes a line), the page will scroll slightly.
    // This causes the composer to overflow and be pushed out of view.
    //
    // We can detect this happening by observing a sequence of events:
    //  1. First, we'll only want to be tracking this while the
    //     composer has focus.
    //  2. The composer height shrinks.
    //  3. The visual viewport emits a scroll event.
    // When that happens, we'll scroll the composer back into view.

    // We're specifically looking for scroll events on the visual viewport.
    const visualViewportScroll$ = window.visualViewport
      ? fromEvent(window.visualViewport, 'scroll')
      : NEVER

    // Emits a value when the composer height shrinks
    const composerHeightShrinkage$ = composerSize$.pipe(
      map((size) => size.height),
      distinctUntilChanged(),
      pairwise(),
      filter(([prevHeight, nextHeight]) => prevHeight > nextHeight),
    )

    // Wire it all together. When the composer shrinks...
    const fixOverflowOnComposerShrinkage$ = composerHeightShrinkage$.pipe(
      // ... then wait for a single visual viewport scroll event
      switchMap(() => visualViewportScroll$.pipe(take(1))),
      // ... but only while the composer is active.
      whileActive(isComposerFocused$),
      tap(() => {
        // If the sentinel ref is set, then we're using the sentinel.
        // iOS Safari won't scroll a `fixed` element into view, so we'll
        // trigger it on the sentinel instead.
        const target = composerSentinelRef.current ?? composerRef.current
        target?.scrollIntoView()
      }),
    )

    // Subscribe to all pipelines.
    const subscription = merge(
      blurOnTouchScroll$,
      fixOverflowOnComposerShrinkage$,
    ).subscribe()
    return () => subscription.unsubscribe()
  }, [blur$, isComposerFocused$, scrollingElement, composerSize$])

  const typingIndicator = renderTypingNames(typing)

  return (
    <LazyMotion features={loadMotionFeatures}>
      <Provider
        values={[
          [
            ConversationMessageComposerContext,
            {
              blur$,
              onFocusChange: composerFocusChange,
            },
          ],
        ]}
      >
        <div
          className={cx(styles.root, spacing === 'compact' && styles.compact)}
          data-test={testId}
        >
          {header}

          <div className={styles.inner}>
            <m.div
              transition={shuffleTransition}
              initial={{ opacity: 0 }}
              animate={{
                opacity: isReadyToAcceptScrollRequests ? 1 : 0,
              }}
              className={styles.leadingContent}
            >
              {children}
            </m.div>

            {messageList && (
              <m.div
                className={styles.messageList}
                transition={shuffleTransition}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: isReadyToAcceptScrollRequests ? 1 : 0,
                }}
              >
                {messageList}
              </m.div>
            )}

            {!disallowScrollingToBottom && isReadyToAcceptScrollRequests && (
              <m.div
                className={styles.scrollToBottomContainer}
                inert={scrollButtonInert}
                style={{
                  y: scrollButtonContainerYWithOffset,
                  opacity: scrollButtonContainerOpacity,
                }}
              >
                <RawButton
                  onPress={scrollToBottomPressHandler}
                  className={({ isHovered, isPressed, isFocusVisible }) =>
                    cx(
                      styles.scrollToBottomButton,
                      isHovered && styles.hover,
                      isPressed && styles.pressed,
                      isFocusVisible && styles.focusVisible,
                    )
                  }
                >
                  <Icon name="chevron" sm rotate={90} />
                  {scrollButtonLabel}
                </RawButton>
              </m.div>
            )}

            <LayoutGroup>
              {composerPositioning === 'fixed' && (
                // Sentinel element used for locating the point at which
                // to scroll to when keeping the composer visible, as well
                // as taking up space to make the fixed element appear as
                // part of the flow.
                <m.div
                  ref={connectSentinelRef}
                  className={styles.composerSentinel}
                  style={{
                    // Automatically size the sentinel based on the real
                    // composer's height.
                    height: composerSizeMotionValues.height,
                  }}
                />
              )}

              <m.div
                layoutRoot={composerPositioning === 'fixed'}
                ref={connectComposerRef}
                className={cx(
                  styles.composerContainer,
                  isComposerFocused && styles.focused,
                )}
                transition={shuffleTransition}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: isReadyToAcceptScrollRequests ? 1 : 0,
                }}
                style={
                  composerPositioning === 'fixed'
                    ? {
                        width: sentinelWidth,
                      }
                    : undefined
                }
              >
                {composerMask === 'visible' && (
                  <m.div
                    // Only animate the mask when using fixed positioning,
                    // because with `sticky` it'll show a snapshot of the
                    // previous content while it's animating
                    // which looks like ass.
                    layout={composerPositioning === 'fixed'}
                    transition={shuffleTransition}
                    className={styles.composerMask}
                    animate={{
                      opacity: isComposerHidden ? 0 : 1,
                      transition: {
                        opacity: {
                          delay: isComposerHidden ? 0 : 0.5,
                        },
                      },
                    }}
                  />
                )}

                <m.div
                  className={styles.composerLayer}
                  initial={false}
                  inert={isComposerHidden}
                  aria-hidden={isComposerHidden}
                  transition={shuffleTransition}
                  animate={{
                    opacity: isComposerHidden ? 0 : 1,
                  }}
                >
                  <div className={styles.composerContent}>
                    <m.div
                      layout="position"
                      transition={shuffleTransition}
                      className={styles.typingIndicatorContainer}
                    >
                      <AnimatePresence>
                        {typingIndicator && (
                          <m.div
                            className={styles.typingIndicator}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{
                              opacity: 0,
                              transition: { duration: 0.1 },
                            }}
                          >
                            {typingIndicator}
                            <Dots />
                          </m.div>
                        )}
                      </AnimatePresence>
                    </m.div>
                    <div className={styles.composer}>
                      {composer ?? <div style={{ height: 20 }} />}
                    </div>
                  </div>
                </m.div>
              </m.div>
            </LayoutGroup>
          </div>
        </div>
      </Provider>
    </LazyMotion>
  )
}

/**
 * Renders a list of dots that animate.
 */
function Dots() {
  return (
    <>
      <span className={styles.typingDot}>.</span>
      <span className={styles.typingDot}>.</span>
      <span className={styles.typingDot}>.</span>
    </>
  )
}

/**
 * Renders the names of the people who are currently typing.
 *
 * @param names
 */
function renderTypingNames(names?: string[]): ReactNode {
  if (!names?.length) {
    return null
  }

  if (names.length === 1) {
    return (
      <>
        <span className="text-strong">{names[0]}</span> is typing
      </>
    )
  }

  if (names.length > 4) {
    return (
      <>
        <span className="text-strong">Several people</span> are typing
      </>
    )
  }

  const last = names[names.length - 1]
  return (
    <>
      {names.slice(0, -1).map((name, i) => (
        <Fragment key={i}>
          <span key={i} className="text-strong">
            {name}
          </span>
          {i === names.length - 2 ? ' ' : ', '}
        </Fragment>
      ))}
      and <span className="text-strong">{last}</span> are typing
    </>
  )
}
